import { useEffect, useState, FC } from "react";

import { ExpandMore, Add, Delete, Save, Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  Tabs,
  Tab,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  getLinterSchema,
  patchLinterSchema,
  LinterSchemaData,
  LinterSchemaModel,
  LinterCategory,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { Link } from "react-router-dom";

import { Breadcrumbs, Crumb, DevPage, Section } from "./common";
import "./dev.scss";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

// Simple list editor for prefixes and exempt fields
const SimpleListEditor: FC<{
  items: string[];
  onChange: (items: string[]) => void;
  label: string;
  placeholder: string;
}> = ({ items, onChange, label, placeholder }) => {
  const [newItem, setNewItem] = useState("");

  const handleAdd = () => {
    if (newItem.trim() && !items.includes(newItem.trim())) {
      onChange([...items, newItem.trim()]);
      setNewItem("");
    }
  };

  const handleDelete = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {items.map((item, index) => (
          <Chip
            key={index}
            label={item}
            onDelete={() => handleDelete(index)}
            variant="outlined"
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          size="small"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button variant="outlined" onClick={handleAdd} startIcon={<Add />}>
          Add
        </Button>
      </Box>
    </Box>
  );
};

// Category accordion component
const CategoryEditor: FC<{
  category: LinterCategory;
  onChange: (category: LinterCategory) => void;
  onDelete: () => void;
}> = ({ category, onChange, onDelete }) => {
  const [newRole, setNewRole] = useState({ name: "", label: "" });
  const [newField, setNewField] = useState({ name: "", label: "" });

  const handleAddRole = () => {
    const trimmedName = newRole.name.trim();
    const trimmedLabel = newRole.label.trim();
    if (!trimmedName || !trimmedLabel) return;
    // Block duplicates
    if (category.roles.some((r) => r.name === trimmedName)) {
      toast.error(`Role "${trimmedName}" already exists`);
      return;
    }
    onChange({
      ...category,
      roles: [...category.roles, { name: trimmedName, label: trimmedLabel }],
    });
    setNewRole({ name: "", label: "" });
  };

  const handleDeleteRole = (name: string) => {
    onChange({
      ...category,
      roles: category.roles.filter((r) => r.name !== name),
    });
  };

  const handleAddField = () => {
    const trimmedName = newField.name.trim();
    const trimmedLabel = newField.label.trim();
    if (!trimmedName || !trimmedLabel) return;
    // Block duplicates
    if (category.fields.some((f) => f.name === trimmedName)) {
      toast.error(`Field "${trimmedName}" already exists`);
      return;
    }
    onChange({
      ...category,
      fields: [...category.fields, { name: trimmedName, label: trimmedLabel }],
    });
    setNewField({ name: "", label: "" });
  };

  const handleDeleteField = (name: string) => {
    onChange({
      ...category,
      fields: category.fields.filter((f) => f.name !== name),
    });
  };

  const roleColumns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "label", headerName: "Label", flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDeleteRole(params.row.name)}
        >
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const fieldColumns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "label", headerName: "Label", flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDeleteField(params.row.name)}
        >
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Use name as stable id (duplicates are now blocked on add)
  const rolesWithIds = [...category.roles]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((role) => ({ ...role, id: role.name }));

  const fieldsWithIds = [...category.fields]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((field) => ({ ...field, id: field.name }));

  return (
    <Accordion disableGutters sx={{ "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <Typography variant="h6">{category.name}</Typography>
          <Chip
            label={`${category.roles.length} roles`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${category.fields.length} fields`}
            size="small"
            color="secondary"
            variant="outlined"
          />
          {category.requiresRole && (
            <Chip label="Requires Role" size="small" color="warning" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Category Properties */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Name"
              size="small"
              value={category.name}
              onChange={(e) => onChange({ ...category, name: e.target.value })}
            />
            <TextField
              label="Plural"
              size="small"
              value={category.plural}
              onChange={(e) =>
                onChange({ ...category, plural: e.target.value })
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={category.requiresRole}
                  onChange={(e) =>
                    onChange({ ...category, requiresRole: e.target.checked })
                  }
                />
              }
              label="Requires Role"
            />
            <Button
              color="error"
              variant="outlined"
              onClick={onDelete}
              startIcon={<Delete />}
            >
              Delete
            </Button>
          </Box>

          {/* Roles Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Roles ({category.roles.length})
            </Typography>
            <DataGrid
              rows={rolesWithIds}
              columns={roleColumns}
              autoHeight
              rowSelection={false}
              disableColumnMenu
              hideFooter={rolesWithIds.length <= 10}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 25, 50]}
              localeText={{ noRowsLabel: "No roles defined" }}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="roleName"
                value={newRole.name}
                onChange={(e) =>
                  setNewRole({ ...newRole, name: e.target.value })
                }
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="Role Label"
                value={newRole.label}
                onChange={(e) =>
                  setNewRole({ ...newRole, label: e.target.value })
                }
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={handleAddRole}
                disabled={!newRole.name.trim() || !newRole.label.trim()}
              >
                Add
              </Button>
            </Box>
          </Box>

          {/* Fields Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Fields ({category.fields.length})
            </Typography>
            <DataGrid
              rows={fieldsWithIds}
              columns={fieldColumns}
              autoHeight
              rowSelection={false}
              disableColumnMenu
              hideFooter={fieldsWithIds.length <= 10}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 25, 50]}
              localeText={{ noRowsLabel: "No fields defined" }}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="fieldName"
                value={newField.name}
                onChange={(e) =>
                  setNewField({ ...newField, name: e.target.value })
                }
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="Field Label"
                value={newField.label}
                onChange={(e) =>
                  setNewField({ ...newField, label: e.target.value })
                }
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={handleAddField}
                disabled={!newField.name.trim() || !newField.label.trim()}
              >
                Add
              </Button>
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// Add Category Dialog
const AddCategoryDialog: FC<{
  open: boolean;
  onClose: () => void;
  onAdd: (category: LinterCategory) => void;
}> = ({ open, onClose, onAdd }) => {
  const [name, setName] = useState("");
  const [plural, setPlural] = useState("");
  const [requiresRole, setRequiresRole] = useState(true);

  const handleAdd = () => {
    if (name.trim() && plural.trim()) {
      onAdd({
        name: name.trim(),
        plural: plural.trim(),
        requiresRole,
        roles: [],
        fields: [],
      });
      setName("");
      setPlural("");
      setRequiresRole(true);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add Category</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Category Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="person"
            helperText="Singular form, e.g., 'person', 'vehicle'"
          />
          <TextField
            label="Plural Form"
            value={plural}
            onChange={(e) => setPlural(e.target.value)}
            placeholder="persons"
            helperText="For paneldynamic naming, e.g., 'persons', 'vehicles'"
          />
          <FormControlLabel
            control={
              <Switch
                checked={requiresRole}
                onChange={(e) => setRequiresRole(e.target.checked)}
              />
            }
            label="Requires Role"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained">
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const LinterSchemaPage: FC = () => {
  const [schema, setSchema] = useState<LinterSchemaModel | null>(null);
  const [schemaData, setSchemaData] = useState<LinterSchemaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSchema = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiError } = await getLinterSchema();
      if (apiError || !data) {
        throw new Error(apiError?.msg || "Failed to fetch schema");
      }
      setSchema(data.schema);
      setSchemaData(data.schema.schema_data);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load schema");
      toast.error("Failed to load linter schema");
    } finally {
      setLoading(false);
    }
  };

  const saveSchema = async () => {
    if (!schemaData) return;
    setSaving(true);
    try {
      const { data, error: apiError } = await patchLinterSchema({
        body: { schemaData },
      });
      if (apiError || !data) {
        throw new Error(apiError?.msg || "Failed to save schema");
      }
      setSchema(data.schema);
      setSchemaData(data.schema.schema_data);
      setHasChanges(false);
      toast.success("Linter schema saved successfully");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save schema");
      toast.error("Failed to save linter schema");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSchema();
  }, []);

  const updateSchemaData = (newData: LinterSchemaData) => {
    setSchemaData(newData);
    setHasChanges(true);
  };

  const handleCategoryChange = (index: number, category: LinterCategory) => {
    if (!schemaData) return;
    const newCategories = [...schemaData.categories];
    newCategories[index] = category;
    updateSchemaData({ ...schemaData, categories: newCategories });
  };

  const handleDeleteCategory = (index: number) => {
    if (!schemaData) return;
    updateSchemaData({
      ...schemaData,
      categories: schemaData.categories.filter((_, i) => i !== index),
    });
  };

  const handleAddCategory = (category: LinterCategory) => {
    if (!schemaData) return;
    updateSchemaData({
      ...schemaData,
      categories: [...schemaData.categories, category],
    });
  };

  if (loading) {
    return (
      <DevPage title="Linter Schema">
        <Section>
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        </Section>
      </DevPage>
    );
  }

  return (
    <DevPage
      title="Linter Schema"
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>Linter Schema</Crumb>
        </Breadcrumbs>
      }
    >
      <Section>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box>
            {schema && (
              <Typography variant="body2" color="text.secondary">
                Version: {schema.version}
                {schema.time_updated && (
                  <>
                    {" "}
                    | Last updated:{" "}
                    {new Date(schema.time_updated).toLocaleString()}
                  </>
                )}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchSchema}
              disabled={loading}
            >
              Reload
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={saveSchema}
              disabled={saving || !hasChanges}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>

        {hasChanges && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have unsaved changes
          </Alert>
        )}

        <Paper sx={{ width: "100%" }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
          >
            <Tab label="Categories" />
            <Tab label="System Prefixes" />
            <Tab label="Exempt Fields" />
            <Tab label="How it Works" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {schemaData && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddCategoryOpen(true)}
                  >
                    Add Category
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  {/* Track original index for stable updates when names are edited */}
                  {schemaData.categories
                    .map((category, originalIndex) => ({
                      category,
                      originalIndex,
                    }))
                    .sort((a, b) =>
                      a.category.name.localeCompare(b.category.name),
                    )
                    .map(({ category, originalIndex }) => (
                      <CategoryEditor
                        key={originalIndex}
                        category={category}
                        onChange={(cat) =>
                          handleCategoryChange(originalIndex, cat)
                        }
                        onDelete={() => handleDeleteCategory(originalIndex)}
                      />
                    ))}
                </Box>
                <AddCategoryDialog
                  open={addCategoryOpen}
                  onClose={() => setAddCategoryOpen(false)}
                  onAdd={handleAddCategory}
                />
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {schemaData && (
              <SimpleListEditor
                items={schemaData.systemPrefixes}
                onChange={(prefixes) =>
                  updateSchemaData({ ...schemaData, systemPrefixes: prefixes })
                }
                label="System Prefixes"
                placeholder="e.g., page, panel, _"
              />
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {schemaData && (
              <SimpleListEditor
                items={schemaData.exemptFields}
                onChange={(fields) =>
                  updateSchemaData({ ...schemaData, exemptFields: fields })
                }
                label="Exempt Fields"
                placeholder="e.g., locale, completedHtml"
              />
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ maxWidth: 800 }}>
              <Typography variant="h6" gutterBottom>
                Field Naming Convention
              </Typography>
              <Typography paragraph>
                The linter validates that survey field names follow a consistent
                naming pattern. This ensures data is structured predictably for
                reporting, exports, and integrations.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                Basic Pattern
              </Typography>
              <Typography paragraph>
                Field names follow the format:{" "}
                <code>category-role_fieldName</code>
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <li>
                  <strong>Category</strong> - The type of entity (e.g., person,
                  vehicle, incident)
                </li>
                <li>
                  <strong>-</strong> (hyphen) - Separates category from role
                </li>
                <li>
                  <strong>Role</strong> - The role within that category (e.g.,
                  reportingParty, suspect, witness)
                </li>
                <li>
                  <strong>_</strong> (underscore) - Separates role from field
                  name
                </li>
                <li>
                  <strong>Field Name</strong> - The specific data field (e.g.,
                  firstName, lastName, dob)
                </li>
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                Examples
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "action.hover",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                }}
              >
                {`person-reportingParty_firstName   → person + reportingParty + firstName
person-suspect_dob                → person + suspect + dob
vehicle-suspect_make              → vehicle + suspect + make
business-reportingParty_name      → business + reportingParty + name
incident_narrative                → incident + narrative (no role needed)`}
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                Panel Dynamic Arrays
              </Typography>
              <Typography paragraph>
                For repeating sections (multiple persons, vehicles, etc.), use
                the plural form as the panel name:
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "action.hover",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                }}
              >
                {`Panel name: "persons-suspect"     → Contains suspect person fields
Panel name: "vehicles-involved"   → Contains involved vehicle fields`}
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                System Prefixes
              </Typography>
              <Typography paragraph>
                Fields starting with system prefixes are automatically skipped
                by the linter. These are used for layout and internal SurveyJS
                functionality:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <li>
                  <code>_</code> - Internal/hidden fields
                </li>
                <li>
                  <code>page</code>, <code>panel</code>, <code>row</code>,{" "}
                  <code>col</code> - Layout elements
                </li>
                <li>
                  <code>sv_</code> - SurveyJS system fields
                </li>
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                Exempt Fields
              </Typography>
              <Typography paragraph>
                Some field names are exempt from validation because they are
                standard SurveyJS configuration properties (e.g.,{" "}
                <code>locale</code>, <code>completedHtml</code>).
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                Making Changes
              </Typography>
              <Typography paragraph>
                When you add or modify categories, roles, or fields here,
                surveys using those naming patterns will pass validation.
                Changes take effect immediately after saving.
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Changes here affect validation only. Existing survey data is not
                modified - this controls what field names are considered valid
                when editing surveys.
              </Alert>
            </Box>
          </TabPanel>
        </Paper>
      </Section>
    </DevPage>
  );
};
